import React from "react";
import { UserRound, GraduationCap, Fingerprint } from "lucide-react";
import { MapPinHouse } from "lucide-react"; // Using MapPin instead of MapPinHouse
import { Church } from "lucide-react"; // Using Church instead of Signpost for religion
import { AboutValue } from "util/userUtils";
import { LocationData } from "ui/LocationSearchField";

// Define the props for the component
interface UserBackgroundTileProps {
  aboutDetails: {
    about_bio_age?: AboutValue<number>;
    about_bio_gender?: AboutValue<string>;
    about_bio_location?: AboutValue<LocationData | string>; // Updated to support both LocationData and string
    about_bio_education?: AboutValue<string>;
    about_bio_religion?: AboutValue<string[]>;
    about_bio_political_identities?: AboutValue<string[]>;
    [key: string]: AboutValue<any> | undefined;
  };
}
// Define option types for the dropdown fields
interface OptionType {
  value: string;
  label: string;
}
/**
 * Component to display a user's background information with icons
 * 
 * @param aboutDetails - An object containing the user's about properties with privacy levels
 * @returns A React component displaying the user's background information
 */
const UserBackgroundTile: React.FC<UserBackgroundTileProps> = ({ aboutDetails }) => {
  /**
   * Helper function to format education value for display
   * @param education - Raw education value from the API
   * @returns Formatted education text for display
   */
  const formatEducation = (education: string): string => {
    const educationMap: Record<string, string> = {
      "none": "None",
      "some_high_school": "Some high school",
      "high_school": "High school",
      "some_college": "Some college",
      "trade_school": "Trade school",
      "associates_degree": "Associate's degree",
      "bachelors_degree": "Bachelor's degree",
      "masters_degree": "Master's degree",
      "doctoral_degree": "Doctoral degree",
      "other": "Other",
      "rather_not_say": "Rather not say"
    };
    
    return educationMap[education] || education;
  };

  /**
   * Helper function to format location data
   * @param locationData - Location data object or string
   * @returns Formatted location string (city, state, country)
   */
  const formatLocation = (locationData: LocationData | string): string => {
    if (!locationData) return '';
    
    // If locationData is a string (backward compatibility), return it directly
    if (typeof locationData === 'string') {
      return locationData;
    }

    // If we have a sanitized neighborhood-level formatted address, prefer it as-is.
    // This preserves the intended display such as: "Victoria Estates, Atlanta, GA USA".
    if (locationData.neighborhood && locationData.formatted_address) {
      return locationData.formatted_address;
    }
    
    // If it's a LocationData object, format it appropriately
    const parts = [];
    
    // If there's a specific name (establishment, landmark, etc), prioritize it
    if (locationData.name) {
      return locationData.name;
    }
    
    if (locationData.city) parts.push(locationData.city);
    if (locationData.state) parts.push(locationData.state);
    if (locationData.country) parts.push(locationData.country);
    
    // If we have a formatted address and no parts, use that
    if (parts.length === 0 && locationData.formatted_address) {
      return locationData.formatted_address;
    }
    
    return parts.join(', ');
  };

  // Religion options
  const religionOptions: OptionType[] = [
    { value: "atheism", label: "Atheism" },
    { value: "agnosticism", label: "Agnosticism" },
    { value: "christianity", label: "Christianity" },
    { value: "islam", label: "Islam" },
    { value: "hinduism", label: "Hinduism" },
    { value: "buddhism", label: "Buddhism" },
    { value: "shinto", label: "Shinto" },
    { value: "taoism", label: "Taoism" },
    { value: "sikhism", label: "Sikhism" },
    { value: "judaism", label: "Judaism" },
    { value: "naturalism_religious", label: "Naturalism (religious)" },
    { value: "naturalism_non_religious", label: "Naturalism (non-religious)" },
    { value: "other", label: "Other" },
  ]

  // Political identity options
  const politicalIdentityOptions: OptionType[] = [
    { value: "centrist", label: "Centrist" },
    { value: "moderate", label: "Moderate" },
    { value: "right-wing", label: "Right-wing" },
    { value: "left-wing", label: "Left-wing" },
    { value: "liberal", label: "Liberal" },
    { value: "neoliberal", label: "Neoliberal" },
    { value: "conservative", label: "Conservative" },
    { value: "progressive", label: "Progressive" },
    { value: "traditionalist", label: "Traditionalist" },
    { value: "libertarian", label: "Libertarian" },
    { value: "populist", label: "Populist" },
    { value: "nationalist", label: "Nationalist" },
    { value: "social_democrat", label: "Social Democrat" },
    { value: "communist", label: "Communist" },
    { value: "capitalist", label: "Capitalist" },
    { value: "anarchist", label: "Anarchist" },
    { value: "antifascist", label: "Antifascist" },
    { value: "fascist", label: "Fascist" },
    { value: "monarchist", label: "Monarchist" },
    { value: "environmentalist", label: "Environmentalist" },
    { value: "feminist", label: "Feminist" },
    { value: "internationalist", label: "Internationalist" },
    { value: "radical", label: "Radical" },
    { value: "marxist", label: "Marxist" },
    { value: "leninist", label: "Leninist" },
    { value: "corporatist", label: "Corporatist" },
    { value: "decentralist", label: "Decentralist" },
    { value: "technocrat", label: "Technocrat" },
    { value: "other", label: "Other" },
  ]

  /**
   * Helper function to capitalize and format a string
   * @param value - String to format
   * @returns Formatted string with first letter capitalized
   */
  const formatString = (value: string): string => {
    return value.charAt(0).toUpperCase() + value.slice(1);
  };

  const getReligionOptions = (religion: string[]): OptionType[] => {
    return religionOptions.filter(option => religion.includes(option.value));
  }

  const getPoliticalIdentitiesOptions = (politicalIdentities: string[]): OptionType[] => {
    return politicalIdentityOptions.filter(option => politicalIdentities.includes(option.value));
  }

  /**
   * Helper function to render a background item with an icon
   * @param icon - The icon component to display
   * @param content - The content to display
   * @returns A JSX element with the icon and content
   */
  const renderBackgroundItem = (
    icon: React.ReactNode, 
    content: React.ReactNode,
    testId?: string
  ) => {
    if (!content) return null;
    
    return (
      <div className="flex items-center mb-3 last:mb-0" data-testid={testId}>
        <div className="flex items-center justify-center size-8 mr-3 text-eoe-text-meta dark:text-eoe-dark-text-meta">
          {icon}
        </div>
        <div className="text-sm text-eoe-text dark:text-eoe-dark-text">
          {content}
        </div>
      </div>
    );
  };

  // Extract and prepare values
  const age = aboutDetails?.about_bio_age?.value;
  const gender = aboutDetails?.about_bio_gender?.value && aboutDetails?.about_bio_gender?.value !== "rather_not_say" ? aboutDetails?.about_bio_gender?.value : null;
  const ageGender = (age || gender) ? 
    `${age || ""}${age && gender ? " | " : ""}${gender ? formatString(gender) : ""}` : 
    null;
  
  // Format location from either LocationData object or string
  const locationValue = aboutDetails?.about_bio_location?.value;
  const formattedLocation = locationValue ? formatLocation(locationValue) : null;
  
  const education = aboutDetails?.about_bio_education?.value && aboutDetails?.about_bio_education?.value !== "rather_not_say" ? 
    formatEducation(aboutDetails.about_bio_education.value) : 
    null;
    
  const religion = aboutDetails?.about_bio_religion?.value?.length ? 
    getReligionOptions(aboutDetails.about_bio_religion.value).map(r => r.label).join(", ") : 
    null;
  
  const politicalIdentities = aboutDetails?.about_bio_political_identities?.value?.length ? 
    getPoliticalIdentitiesOptions(aboutDetails.about_bio_political_identities.value).map(p => p.label).join(", ") : 
    null;

  // If no data is available, show a message
  if (!ageGender && !formattedLocation && !education && !religion && !politicalIdentities) {
    return (
      <div className="py-4 text-sm text-center text-eoe-text-sub dark:text-eoe-dark-text-sub">
        This user has not shared any information.
      </div>
    );
  }

  return (
    <div className="py-3 px-1">
      {renderBackgroundItem(
        <UserRound size={20} />, 
        ageGender,
        "user-age-gender"
      )}
      
      {renderBackgroundItem(
        <MapPinHouse size={20} />, 
        formattedLocation,
        "user-location"
      )}
      
      {renderBackgroundItem(
        <GraduationCap size={20} />, 
        education,
        "user-education"
      )}
      
      {renderBackgroundItem(
        <Church size={20} />, 
        religion,
        "user-religion"
      )}
      
      {renderBackgroundItem(
        <Fingerprint size={20} />, 
        politicalIdentities,
        "user-political-identities"
      )}
    </div>
  );
};

export default UserBackgroundTile;
